/**
 * CueQueue (DESIGN-client §1.2): ordered, promise-serialized dispatch of server cues to the
 * scene. Handlers register per cue type; each cue is awaited before the next starts. Sync
 * listeners get `start` (audio, HUD, announcer) and `end` around every cue. When the backlog
 * exceeds `instantThreshold` (or `forceInstant` is set) handlers receive `instant: true` and
 * should jump to their final state. `flush()` aborts the running handler and drops the
 * backlog (reconnect: flush → hydrate from schema → resume). `presentation.cueBusy` mirrors
 * whether a drain is in progress.
 */
import type { Cue, CueType } from './types-local';

export interface CueContext {
  instant: boolean;
  signal: AbortSignal;
  /**
   * Multiplier on every authored beat: 1 normally, 2 while the queue is catching up. Handlers that
   * wait divide by it (`scene/cues/tableCues.ts`); handlers that do not can ignore it. Optional so
   * a hand-built context in a test stays valid.
   */
  rate?: number;
}
export type CueHandler<T extends CueType = CueType> = (cue: Cue<T>, ctx: CueContext) => Promise<void> | void;
export type CueListener = (cue: Cue, ctx: CueContext) => void;

export interface CueQueueOptions {
  /** Called with true when a drain starts and false when the queue is idle again. */
  setBusy?: (busy: boolean) => void;
  /** Called when a handler throws/rejects (default: console.warn). Errors never stall the queue. */
  onError?: (err: unknown, cue: Cue) => void;
  instantThreshold?: number;
  /**
   * Cue types to hold rather than drop while nothing has registered a handler for them.
   *
   * The scene is a lazy chunk: it awaits the deck atlas, the vfx sheet and the fonts before it
   * registers anything, and quick play deals immediately. The first `dealt` of a session was
   * therefore consumed by a queue with no handler for it, so the opening beat of the game — the
   * riffle, the cards arriving — never played, on every player's first hand. Measured by the
   * pacing audit (docs/PACING-AUDIT.md §1).
   *
   * **Opt in.** It defaults to holding NOTHING, and the app turns it on in `net/cues.ts` with
   * `DEFAULT_HOLD_UNTIL_HANDLED`. Holding by default made a bare `new CueQueue()` wait ten seconds
   * for a handler that a unit test was never going to register, so `await q.idle()` hung and
   * `routing.test.ts` had to register a no-op to get its cues through. A queue that nobody has told
   * to expect a scene should not wait for one.
   */
  holdUntilHandled?: readonly CueType[];
  /**
   * Cue types whose handlers are pure state writes and cost nothing to drain. They are excluded
   * from the backlog count that escalates to `instant`, because counting them made a burst of
   * cheap cues collapse the expensive ones that arrived with them (audit §3: 21 of 52 `cardPlayed`
   * collapsed at TIMER_SCALE=0.5).
   */
  freeCueTypes?: readonly CueType[];
  /**
   * How long one handler may take before it is abandoned. Well above any legitimate animation
   * (the longest is the hand-score count-up), so hitting it means a handler is stuck. 0 disables.
   */
  handlerTimeoutMs?: number;
  /**
   * How far behind the server the queue may fall, in ms, before it starts hurrying.
   *
   * Backlog measured in *cues* is the wrong unit: four `timer` writes and one `trickWon` are five
   * cues and one animation. What the player feels is the delay between the server saying a thing
   * happened and the table showing it, so that is what is measured — the age of the oldest cue
   * still waiting. Past `slowBehindMs` every authored beat runs at double speed; past
   * `instantBehindMs` handlers jump to their final state until the queue is level again.
   */
  slowBehindMs?: number;
  instantBehindMs?: number;
  /** Injectable clock (tests). */
  now?: () => number;
}

/** Behind by this much and the beats run at 2×. */
export const DEFAULT_SLOW_BEHIND_MS = 1_200;
/** Behind by this much and the beats are skipped entirely. */
export const DEFAULT_INSTANT_BEHIND_MS = 3_000;
/** How much faster beats run while catching up. */
export const CATCH_UP_RATE = 2;

export const DEFAULT_HANDLER_TIMEOUT_MS = 15_000;

/** Cues worth waiting for a handler to appear: the opening of a hand, once per hand. */
export const DEFAULT_HOLD_UNTIL_HANDLED: readonly CueType[] = ['dealt'];

/** How long a held cue waits before being let through anyway, so a missing handler cannot wedge. */
export const HOLD_TIMEOUT_MS = 10_000;

/** Handlers that are pure state writes; see `freeCueTypes`. */
export const DEFAULT_FREE_CUE_TYPES: readonly CueType[] = ['timer'];

export class CueQueue {
  instantThreshold: number;
  handlerTimeoutMs: number;
  forceInstant = false;
  slowBehindMs: number;
  instantBehindMs: number;

  private readonly holdUntilHandled: ReadonlySet<CueType>;
  private readonly freeCueTypes: ReadonlySet<CueType>;
  private readonly now: () => number;
  private heldSince = 0;
  /** When each queued cue was pushed, so "how far behind" is a time and not a count. */
  private readonly pushedAt = new Map<number, number>();
  /** Every cue up to this seq drains instantly: `catchUp()` sets it. */
  private catchUpTo = -1;

  private readonly handlers = new Map<CueType, CueHandler>();
  private readonly startListeners = new Set<CueListener>();
  private readonly endListeners = new Set<CueListener>();
  private readonly queue: Cue[] = [];
  private readonly setBusy: (busy: boolean) => void;
  private readonly onError: (err: unknown, cue: Cue) => void;
  private draining = false;
  private current: { cue: Cue; ac: AbortController } | null = null;
  private idleResolvers: Array<() => void> = [];
  private lastSeq = -1;
  private applied = 0;
  private appliedSeq = -1;

  constructor(opts: CueQueueOptions = {}) {
    this.instantThreshold = opts.instantThreshold ?? 6;
    this.handlerTimeoutMs = opts.handlerTimeoutMs ?? DEFAULT_HANDLER_TIMEOUT_MS;
    this.slowBehindMs = opts.slowBehindMs ?? DEFAULT_SLOW_BEHIND_MS;
    this.instantBehindMs = opts.instantBehindMs ?? DEFAULT_INSTANT_BEHIND_MS;
    this.now = opts.now ?? (() => Date.now());
    // Empty unless asked: see `holdUntilHandled` above. `net/cues.ts` is what turns it on.
    this.holdUntilHandled = new Set(opts.holdUntilHandled ?? []);
    this.freeCueTypes = new Set(opts.freeCueTypes ?? DEFAULT_FREE_CUE_TYPES);
    this.setBusy = opts.setBusy ?? (() => {});
    this.onError = opts.onError ?? ((err, cue) => console.warn(`[cue] handler for ${cue.type} failed`, err));
  }

  /** Register the scene handler for one cue type (replaces any previous). Returns unregister. */
  register<T extends CueType>(type: T, handler: CueHandler<T>): () => void {
    this.handlers.set(type, handler as CueHandler);
    return () => {
      if (this.handlers.get(type) === (handler as CueHandler)) this.handlers.delete(type);
    };
  }

  registerAll(handlers: { [T in CueType]?: CueHandler<T> }): () => void {
    const offs = (Object.keys(handlers) as CueType[]).map((t) => {
      const h = handlers[t] as CueHandler | undefined;
      return h ? this.register(t, h) : () => {};
    });
    return () => offs.forEach((off) => off());
  }

  hasHandler(type: CueType): boolean {
    return this.handlers.has(type);
  }

  /** Sync listener fired right before a cue's handler runs. */
  onStart(cb: CueListener): () => void {
    this.startListeners.add(cb);
    return () => this.startListeners.delete(cb);
  }

  /** Sync listener fired after a cue's handler settled (also when aborted). */
  onEnd(cb: CueListener): () => void {
    this.endListeners.add(cb);
    return () => this.endListeners.delete(cb);
  }

  push(cue: Cue): void {
    this.queue.push(cue);
    this.pushedAt.set(cue.seq, this.now());
    if (cue.seq > this.lastSeq) this.lastSeq = cue.seq;
    void this.drain();
  }

  pushAll(cues: Iterable<Cue>): void {
    for (const c of cues) this.push(c);
  }

  /** Drop the backlog and abort the running handler. Call before `scene.hydrate(state)`. */
  flush(): void {
    this.queue.length = 0;
    this.pushedAt.clear();
    this.heldSince = 0;
    this.catchUpTo = -1;
    this.current?.ac.abort();
  }

  /**
   * Collapse everything already queued to its final state, then resume normal pacing.
   *
   * Called when the player acts on a table they can already see the future of — their own play.
   * Making their card wait in line behind three bots' animations is what "I clicked and nothing
   * happened" feels like, even once the click is accepted.
   */
  catchUp(): void {
    if (this.lastSeq > this.catchUpTo) this.catchUpTo = this.lastSeq;
  }

  /**
   * How long the oldest cue still waiting has been waiting, in ms. 0 when the queue is level.
   *
   * This, not `pending`, is the honest measure of how far the table is behind the server: a burst
   * of `timer` writes is five cues and no delay, while one `trickWon` is one cue and two seconds.
   */
  get behindMs(): number {
    const head = this.queue[0];
    if (!head) return 0;
    const at = this.pushedAt.get(head.seq);
    return at === undefined ? 0 : Math.max(0, this.now() - at);
  }

  /** Number of cues waiting (not counting the one in flight). */
  get pending(): number {
    return this.queue.length;
  }

  get busy(): boolean {
    return this.draining;
  }

  /** The cue currently being handled, if any. */
  get active(): Cue | null {
    return this.current?.cue ?? null;
  }

  /** Highest cue seq ever pushed (for `resync {sinceSeq}`). */
  get lastPushedSeq(): number {
    return this.lastSeq;
  }

  /** Count of cues whose handler has settled. */
  get appliedCount(): number {
    return this.applied;
  }

  /**
   * Highest cue seq whose handler has settled, or -1. This, not `lastPushedSeq`, is the honest
   * watermark for `resync {sinceSeq}`: a cue that was pushed but dropped by `flush()` was never
   * shown, and asking from the received position would lose it for good.
   */
  get lastAppliedSeq(): number {
    return this.appliedSeq;
  }

  /** Resolves once the queue is empty and no handler is running. */
  idle(): Promise<void> {
    if (!this.draining && this.queue.length === 0) return Promise.resolve();
    return new Promise((resolve) => this.idleResolvers.push(resolve));
  }

  private async drain(): Promise<void> {
    if (this.draining) return;
    this.draining = true;
    this.setBusy(true);
    try {
      while (this.queue.length > 0) {
        const next = this.queue[0]!;
        // Wait for the scene to finish loading rather than throwing the first cue away. Bounded,
        // so a cue type nobody ever handles costs one pause and not the session.
        if (this.holdUntilHandled.has(next.type) && !this.handlers.has(next.type)) {
          const now = Date.now();
          if (this.heldSince === 0) this.heldSince = now;
          if (now - this.heldSince < HOLD_TIMEOUT_MS) {
            await new Promise((r) => setTimeout(r, 25));
            continue;
          }
        }
        this.heldSince = 0;
        // Read the age BEFORE shifting: it is this cue's own wait that says how far behind we are.
        const behind = this.behindMs;
        const cue = this.queue.shift()!;
        this.pushedAt.delete(cue.seq);
        const ac = new AbortController();
        // Only cues that actually cost something to draw count towards the backlog: a burst of
        // free ones would otherwise escalate the expensive ones arriving beside them.
        const backlog = this.queue.reduce((n, c) => (this.freeCueTypes.has(c.type) ? n : n + 1), 0);
        const instant =
          this.forceInstant ||
          cue.seq <= this.catchUpTo ||
          backlog > this.instantThreshold ||
          behind >= this.instantBehindMs;
        // Two speeds, then a jump. Halving the beats recovers a second of drift over a trick
        // without the table teleporting, which is what `instant` looks like to a player.
        const rate = instant ? 1 : behind >= this.slowBehindMs ? CATCH_UP_RATE : 1;
        const ctx: CueContext = { instant, signal: ac.signal, rate };
        this.current = { cue, ac };
        for (const l of this.startListeners) this.safeListener(l, cue, ctx);
        const handler = this.handlers.get(cue.type);
        if (handler) await this.runHandler(handler, cue, ctx, ac);
        this.current = null;
        this.applied++;
        // An aborted cue was never shown, so it must not move the watermark: `flush()` aborts the
        // one in flight, and a resync has to ask for it again.
        if (!ac.signal.aborted && cue.seq > this.appliedSeq) this.appliedSeq = cue.seq;
        for (const l of this.endListeners) this.safeListener(l, cue, ctx);
      }
    } finally {
      this.draining = false;
      this.setBusy(false);
      const resolvers = this.idleResolvers;
      this.idleResolvers = [];
      resolvers.forEach((r) => r());
    }
  }

  /** Run one handler, bounded by the watchdog. Never throws. */
  private async runHandler(handler: CueHandler, cue: Cue, ctx: CueContext, ac: AbortController): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const done = Promise.resolve(handler(cue, ctx)).then(() => 'done' as const);
      if (this.handlerTimeoutMs <= 0) {
        await done;
        return;
      }
      const timedOut = new Promise<'timeout'>((resolve) => {
        timer = setTimeout(() => resolve('timeout'), this.handlerTimeoutMs);
      });
      if ((await Promise.race([done, timedOut])) === 'timeout') {
        ac.abort();
        this.onError(new Error(`cue handler for '${cue.type}' did not settle within ${this.handlerTimeoutMs}ms; abandoning it`), cue);
      }
    } catch (err) {
      if (!ac.signal.aborted) this.onError(err, cue);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  private safeListener(l: CueListener, cue: Cue, ctx: CueContext): void {
    try {
      l(cue, ctx);
    } catch (err) {
      this.onError(err, cue);
    }
  }
}
